import { calculateABSD, calculateBSD, calculateMonthlyMortgage, formatCurrency, formatListingPrice } from "@/lib/portalHelpers";

export const DISTRICT_SEGMENTS = [
  {
    key: "CCR",
    label: "CCR - Prime Central",
    districts: [
      { code: "D01", label: "D01 • Marina / Raffles" },
      { code: "D02", label: "D02 • Tanjong Pagar" },
      { code: "D06", label: "D06 • City Hall" },
      { code: "D09", label: "D09 • Orchard" },
      { code: "D10", label: "D10 • Tanglin / Holland" },
      { code: "D11", label: "D11 • Newton / Novena" },
    ],
  },
  {
    key: "RCR",
    label: "RCR - City Fringe",
    districts: [
      { code: "D03", label: "D03 • Queenstown / Tiong Bahru" },
      { code: "D04", label: "D04 • Harbourfront / Telok Blangah" },
      { code: "D05", label: "D05 • Pasir Panjang / Clementi" },
      { code: "D07", label: "D07 • Bugis / Beach Road" },
      { code: "D08", label: "D08 • Little India" },
      { code: "D12", label: "D12 • Toa Payoh" },
      { code: "D13", label: "D13 • MacPherson" },
      { code: "D14", label: "D14 • Geylang / Eunos" },
      { code: "D15", label: "D15 • East Coast / Marine Parade" },
      { code: "D20", label: "D20 • Bishan / Thomson" },
    ],
  },
  {
    key: "OCR",
    label: "OCR - Suburban / Mass Market",
    districts: [
      { code: "D16", label: "D16 • Bedok" },
      { code: "D17", label: "D17 • Changi" },
      { code: "D18", label: "D18 • Tampines / Pasir Ris" },
      { code: "D19", label: "D19 • Hougang / Punggol" },
      { code: "D21", label: "D21 • Upper Bukit Timah" },
      { code: "D22", label: "D22 • Jurong" },
      { code: "D23", label: "D23 • Bukit Batok / CCK" },
      { code: "D24", label: "D24 • Tengah" },
      { code: "D25", label: "D25 • Woodlands" },
      { code: "D26", label: "D26 • Mandai" },
      { code: "D27", label: "D27 • Yishun" },
      { code: "D28", label: "D28 • Seletar" },
    ],
  },
];

export const DISTRICT_FACTS = {
  D01: "safe-haven demand anchored by Marina Bay office and lifestyle prestige",
  D02: "steady CBD repositioning momentum with strong investor attention",
  D03: "city-fringe resilience driven by liveability and mature-estate demand",
  D04: "waterfront appeal and Greater Southern Waterfront interest",
  D05: "innovation-corridor upside supported by one-north and west-coast demand",
  D06: "rare central stock with strong hospitality and heritage value support",
  D07: "mixed-use revival energy around Bugis and Beach Road",
  D08: "urban-fringe demand with strong rental and city-access support",
  D09: "prime luxury demand with ultra-tight Orchard area supply",
  D10: "blue-chip wealth preservation backed by premier school and landed demand",
  D11: "resilient owner-occupier demand around Novena medical and city access",
  D12: "strong upgrader demand with central convenience and mature estate pull",
  D13: "transformation upside supported by city-fringe accessibility",
  D14: "affordability-led momentum with fast access to key employment nodes",
  D15: "strong East Coast transformation momentum and lifestyle-led demand",
  D16: "steady family demand anchored by east-side connectivity",
  D17: "airport-region upside and long-run Changi growth interest",
  D18: "transport-led demand from Tampines and Pasir Ris regional hubs",
  D19: "upgrader activity backed by Punggol digital district growth",
  D20: "high TEL-driven growth with resilient Thomson-Bishan owner demand",
  D21: "Bukit Timah adjacency and school-led upgrader demand",
  D22: "Jurong transformation upside with west-side employment depth",
  D23: "value-driven family demand with improving suburban connectivity",
  D24: "early-mover interest tied to Tengah town development",
  D25: "northern growth supported by cross-border and industrial demand",
  D26: "scarcity value from landed pockets and low-density surroundings",
  D27: "stable suburban demand with strong family catchment depth",
  D28: "selective growth from Seletar lifestyle and landed-market spillover",
};

export const VIEWING_TIME_SLOTS = {
  AM: ["9:00 AM", "10:00 AM", "11:00 AM"],
  PM: ["2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"],
};

const categoryRank = { new_launch: 0, buy: 1, rent: 2 };

export const getDistrictSegment = (districtCode) => DISTRICT_SEGMENTS.find((segment) => segment.districts.some((district) => district.code === districtCode));

export const getDistrictLabel = (districtCode) =>
  DISTRICT_SEGMENTS.flatMap((segment) => segment.districts).find((district) => district.code === districtCode)?.label || districtCode;

export const rankStarBuys = (listings, districtCode) =>
  [...listings]
    .filter((listing) => listing.district === districtCode)
    .sort((left, right) => {
      if (Boolean(left.featured) !== Boolean(right.featured)) return left.featured ? -1 : 1;
      if ((categoryRank[left.category] ?? 99) !== (categoryRank[right.category] ?? 99)) {
        return (categoryRank[left.category] ?? 99) - (categoryRank[right.category] ?? 99);
      }
      return Number(right.price || 0) - Number(left.price || 0);
    })
    .slice(0, 3);

export const buildSimilarProjects = (inventory, selectedListings, districtCode) => {
  const selectedIds = new Set(selectedListings.map((listing) => listing.id));
  const segment = getDistrictSegment(districtCode);
  const segmentDistricts = new Set(segment?.districts.map((district) => district.code) || []);
  const preferredTypes = new Set(selectedListings.map((listing) => listing.property_type));

  return [...inventory]
    .filter((listing) => !selectedIds.has(listing.id))
    .filter((listing) => segmentDistricts.has(listing.district) || listing.district === districtCode)
    .sort((left, right) => {
      const leftTypeBoost = preferredTypes.has(left.property_type) ? 1 : 0;
      const rightTypeBoost = preferredTypes.has(right.property_type) ? 1 : 0;
      if (leftTypeBoost !== rightTypeBoost) return rightTypeBoost - leftTypeBoost;
      if (Boolean(left.featured) !== Boolean(right.featured)) return left.featured ? -1 : 1;
      return Number(right.price || 0) - Number(left.price || 0);
    })
    .slice(0, 3);
};

export const buildListingAnchorSimilarProjects = (inventory, currentListing) => {
  if (!currentListing?.district) return [];

  const segment = getDistrictSegment(currentListing.district);
  const neighboringDistricts = new Set((segment?.districts || []).map((district) => district.code).filter((code) => code !== currentListing.district));

  const scoreCandidate = (listing) => {
    const typeBoost = listing.property_type === currentListing.property_type ? 0 : 1;
    const categoryBoost = listing.category === currentListing.category ? 0 : 1;
    const priceGap = Math.abs(Number(listing.price || 0) - Number(currentListing.price || 0));
    return [typeBoost, categoryBoost, priceGap * 0.000001, -Number(listing.price || 0)];
  };

  const decorateListing = (listing, contextualBadge = "", signatureUsp = "Signature Match") => ({
    ...listing,
    contextual_badge: contextualBadge,
    signature_usp: signatureUsp,
    neighbor_reason: contextualBadge
      ? `${listing.title} in ${listing.district} offers a more competitive PSF for a similar investment profile.`
      : "",
  });

  const sortBySimilarity = (listings) =>
    [...listings].sort((left, right) => {
      const [leftType, leftCategory, leftGap, leftPrice] = scoreCandidate(left);
      const [rightType, rightCategory, rightGap, rightPrice] = scoreCandidate(right);
      if (leftType !== rightType) return leftType - rightType;
      if (leftCategory !== rightCategory) return leftCategory - rightCategory;
      if (leftGap !== rightGap) return leftGap - rightGap;
      return rightPrice - leftPrice;
    });

  const sameDistrictMatches = sortBySimilarity(
    inventory.filter((listing) => listing.district === currentListing.district && listing.id !== currentListing.id),
  ).map((listing) => decorateListing(listing, "", "Same-District Match"));

  const neighboringMatches = sortBySimilarity(
    inventory.filter((listing) => neighboringDistricts.has(listing.district) && listing.id !== currentListing.id),
  ).map((listing) =>
    decorateListing(
      listing,
      Number(listing.psf || 0) <= Number(currentListing.psf || 0) ? "High-Growth Neighbor" : "Alternative Location",
      Number(listing.psf || 0) <= Number(currentListing.psf || 0) ? "Competitive PSF Edge" : "Neighboring Upside",
    ),
  );

  const widerAreaMatches = sortBySimilarity(
    inventory.filter((listing) => listing.id !== currentListing.id && listing.district !== currentListing.district && !neighboringDistricts.has(listing.district)),
  ).map((listing) => decorateListing(listing, "Alternative Location", "Wider-Area Opportunity"));

  return [...sameDistrictMatches, ...neighboringMatches, ...widerAreaMatches].slice(0, 3);
};

export const buildComparisonInsights = (listings) => {
  if (!listings.length) return [];
  const cheapest = [...listings].sort((left, right) => Number(left.price || 0) - Number(right.price || 0))[0];
  const largest = [...listings].sort((left, right) => Number(right.sqft || 0) - Number(left.sqft || 0))[0];
  const premium = [...listings].sort((left, right) => Number(right.psf || 0) - Number(left.psf || 0))[0];

  return [
    { label: "Best entry point", value: `${cheapest.title} — ${formatListingPrice(cheapest)}` },
    { label: "Largest footprint", value: `${largest.title} — ${Number(largest.sqft || 0).toLocaleString("en-SG")} sqft` },
    { label: "Premium positioning", value: `${premium.title} — ${formatCurrency(premium.psf || 0, 0)} PSF` },
  ];
};

export const buildDistrictResponse = (districtCode) => {
  const label = getDistrictLabel(districtCode);
  const fact = DISTRICT_FACTS[districtCode] || "active buyer and investor demand";
  return `Excellent choice. ${label} is currently seeing ${fact}. Here are the top 3 'Star Buys' currently available in our database:`;
};

export const buildJourneyReportContext = ({ mode, districtLabel, starBuys, similarProjects, anchorListing = null }) => {
  const districtCode = anchorListing?.district || starBuys[0]?.district || similarProjects[0]?.district || "";
  const focusProjects = mode === "compare"
    ? [anchorListing, ...starBuys].filter(Boolean)
    : [anchorListing, ...starBuys.slice(0, 1), ...similarProjects].filter(Boolean);
  const selectedProjects = focusProjects.map((listing) => listing.title).filter(Boolean);

  return {
    selectedProjects,
    comparisonContext: {
      journey_mode: mode,
      district_code: districtCode,
      district_label: districtLabel,
      anchor_project: anchorListing?.title || "",
      compared_projects: starBuys.map((listing) => listing.title),
      revealed_similar_projects: similarProjects.map((listing) => listing.title),
    },
    contextLabel: `${districtLabel} ${mode === "similar" ? "Similar Projects" : "Comparison Report"}`,
  };
};

export const buildViewingTimeline = ({ preferredDate, timeWindow, specificTime, customTime }) => {
  const slot = customTime?.trim() || specificTime || "Time pending";
  return `${preferredDate} (${timeWindow} • ${slot})`;
};

export const calculateTaxSnapshot = ({ residency, propertyCount, purchasePrice }) => {
  const buyerType = residency === "FR" ? "Foreigner" : residency;
  const absdResult = calculateABSD({ amount: purchasePrice || 0, buyerType, propertyCount });
  const bsdDuty = purchasePrice ? calculateBSD(purchasePrice) : 0;
  return {
    absdRate: Math.round((absdResult.rate || 0) * 100),
    absdDuty: absdResult.duty || 0,
    bsdDuty,
  };
};

export const calculateRentVsBuySnapshot = ({ monthlyRent, budget }) => {
  const loanAmount = Number(budget || 0) * 0.75;
  const lowRatePayment = calculateMonthlyMortgage({ principal: loanAmount, annualRate: 2.8, tenureYears: 30 });
  const highRatePayment = calculateMonthlyMortgage({ principal: loanAmount, annualRate: 3.2, tenureYears: 30 });
  const annualRentSpend = Number(monthlyRent || 0) * 12;
  return {
    lowRatePayment,
    highRatePayment,
    annualRentSpend,
    budget: Number(budget || 0),
  };
};